import '@xyflow/react/dist/style.css';
import React, { useCallback, useMemo, useState } from 'react';

// Add CSS to hide ReactFlow attribution
const FlowCanvasStyles = () => {
  return (
    <style>{`
      .react-flow__attribution {
        display: none !important;
      }
    `}</style>
  );
};
import {
  ReactFlow,
  Background,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  ConnectionMode,
  NodeTypes,
  ReactFlowProvider,
  Node,
  Edge,
  useReactFlow
} from '@xyflow/react';
import dagre from 'dagre';

import { Plus, Type, FileText, Image as ImageIcon, Video, Wand2, BookOpen } from 'lucide-react';
import clsx from 'clsx';

import BaseNode from './components/nodes/BaseNode';
import { NODE_REGISTRY } from './config/nodeRegistry';
import ControlPanel from './components/ControlPanel';
import CanvasControls from './components/CanvasControls';
import { CanvasContext } from './context/CanvasContext';
import NodeContextMenu from './components/NodeContextMenu';
import CanvasContextMenu from './components/CanvasContextMenu';
import WorkflowTemplatesDialog, { WorkflowTemplate } from './components/WorkflowTemplatesDialog';
import { DataType } from './types';

// Initial Nodes for demo
const initialNodes: Node[] = [
  { id: '1', type: 'text-node', position: { x: 100, y: 100 }, data: { text: 'A futuristic city with neon lights' } },
  { id: '2', type: 'gen-image', position: { x: 500, y: 50 }, data: { width: 1024, height: 576, batchSize: 1, template: 'Cyberpunk' } }
];
const initialEdges: Edge[] = [
  { id: 'e1-2', source: '1', sourceHandle: 'out_text', target: '2', targetHandle: 'in_text', animated: true, style: { stroke: '#64748b' } }
];

interface MenuState {
  x: number;
  y: number;
  nodeId: string; // The existing node that was clicked
  handleId: string;
  type: DataType;
  isInput: boolean; // True if input handle was clicked
}

interface CanvasMenuState {
  x: number;
  y: number;
}

const Flow = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);
  const [menuData, setMenuData] = useState<MenuState | null>(null);
  const [canvasMenu, setCanvasMenu] = useState<CanvasMenuState | null>(null);
  const [clipboard, setClipboard] = useState<{ nodes: Node[]; edges: Edge[] } | null>(null);
  const [isTemplatesDialogOpen, setIsTemplatesDialogOpen] = useState(false);
  const { getNode, getViewport } = useReactFlow();

  // Register all types to BaseNode
  const nodeTypes: NodeTypes = useMemo(() => {
    const types: NodeTypes = {};
    Object.keys(NODE_REGISTRY).forEach(key => types[key] = BaseNode);
    return types;
  }, []);

  // Helper: Check if a position overlaps with existing nodes
  const isPositionOccupied = useCallback((x: number, y: number, nodeWidth: number = 300, nodeHeight: number = 200): boolean => {
    return nodes.some(node => {
      const nodeW = (node.style?.width || node.measured?.width || nodeWidth) as number;
      const nodeH = (node.style?.height || node.measured?.height || nodeHeight) as number;

      const padding = 20;
      return (
        x < (node.position.x as number) + nodeW + padding &&
        x + nodeWidth + padding > node.position.x &&
        y < (node.position.y as number) + nodeH + padding &&
        y + nodeHeight + padding > node.position.y
      );
    });
  }, [nodes]);

  // Helper: Find a free position by offsetting vertically
  const findFreePosition = useCallback((baseX: number, baseY: number, nodeWidth: number = 300, nodeHeight: number = 200): { x: number; y: number } => {
    let offsetY = 0;
    const verticalStep = 300;
    const maxAttempts = 50;

    for (let i = 0; i < maxAttempts; i++) {
      const testY = baseY + offsetY;
      if (!isPositionOccupied(baseX, testY, nodeWidth, nodeHeight)) {
        return { x: baseX, y: testY };
      }
      offsetY += verticalStep;
    }

    return { x: baseX, y: baseY };
  }, [isPositionOccupied]);

  // Auto Layout: Arrange nodes in layers based on connections using dagre
  const handleAutoLayout = useCallback(() => {
    if (nodes.length === 0) return;

    // ========== Step 1: Categorize nodes ==========
    // Only layout groups and standalone nodes, ignore child nodes
    const groupNodes = nodes.filter(n => n.type === 'group');
    const standaloneNodes = nodes.filter(n => !n.parentId && n.type !== 'group');
    const layoutNodes = [...groupNodes, ...standaloneNodes];

    if (layoutNodes.length === 0) return;

    // ========== Step 2: Calculate node dimensions for all node types ==========
    const getNodeDimensions = (node: Node): { width: number; height: number } => {
      if (node.type === 'group') {
        // Group: Priority measured > style > default
        // NodeResizeControl updates measured when user drags to resize
        const width = (node.measured?.width as number) || (node.style?.width as number) || 300;
        const height = (node.measured?.height as number) || (node.style?.height as number) || 200;
        return { width, height };
      } else {
        // Regular resizable nodes: Priority measured > style > default
        // measured may be inflated by content, but user sets boundaries via NodeResizeControl
        const width = (node.measured?.width as number) || (node.style?.width as number) || 300;
        const height = (node.measured?.height as number) || (node.style?.height as number) || 200;
        return { width, height };
      }
    };

    // ========== Step 3: Initialize dagre graph ==========
    const g = new dagre.graphlib.Graph();
    g.setGraph({
      rankdir: 'LR',       // Left to Right (横向布局)
      nodesep: 80,         // 同层节点水平间距
      ranksep: 120,        // 层级之间垂直间距
      edgesep: 50,         // 边之间的间距
      marginx: 100,        // 画布左右边距
      marginy: 100,        // 画布上下边距
    });

    g.setDefaultEdgeLabel(() => ({}));

    // ========== Step 4: Add nodes to dagre graph ==========
    layoutNodes.forEach(node => {
      const dimensions = getNodeDimensions(node);
      g.setNode(node.id, {
        width: dimensions.width,
        height: dimensions.height,
      });
    });

    // ========== Step 5: Add edges to dagre graph ==========
    edges.forEach(edge => {
      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);

      if (!sourceNode || !targetNode) return;

      // Map to layout-level nodes (group or standalone)
      const effectiveSource = sourceNode.parentId || sourceNode.id;
      const effectiveTarget = targetNode.parentId || targetNode.id;

      // Skip self-connections within the same group
      if (effectiveSource === effectiveTarget) return;

      // Only add if both ends are layout nodes
      if (layoutNodes.some(n => n.id === effectiveSource) &&
        layoutNodes.some(n => n.id === effectiveTarget)) {
        g.setEdge(effectiveSource, effectiveTarget);
      }
    });

    // ========== Step 6: Run dagre layout ==========
    dagre.layout(g);

    // ========== Step 7: Extract positions from dagre ==========
    const positionMap = new Map<string, { x: number; y: number }>();
    const sizeMap = new Map<string, { width: number; height: number }>();

    layoutNodes.forEach(node => {
      const dagreNode = g.node(node.id);
      if (dagreNode) {
        // dagre 返回的是中心点坐标，需要转换为左上角坐标
        const dimensions = getNodeDimensions(node);
        positionMap.set(node.id, {
          x: dagreNode.x - dimensions.width / 2,
          y: dagreNode.y - dimensions.height / 2,
        });
        sizeMap.set(node.id, dimensions);
      }
    });

    // ========== Step 8: Update node positions (child nodes keep relative positions) ==========
    setNodes(currentNodes =>
      currentNodes.map(node => {
        const newPos = positionMap.get(node.id);
        if (newPos) {
          // This is a layout node (group or standalone)
          const size = sizeMap.get(node.id);
          if (node.type === 'group' && size) {
            return {
              ...node,
              position: newPos,
              style: {
                ...(node.style || {}),
                width: size.width,
                height: size.height,
              },
            };
          } else {
            return { ...node, position: newPos };
          }
        }
        // Child nodes: keep their current relative positions, don't change
        return node;
      })
    );
  }, [nodes, edges, setNodes]);

  // Helper: Get max connections for a specific handle
  const getHandleMaxConnections = useCallback((nodeId: string, handleId: string): number => {
    const node = getNode(nodeId);
    if (!node || !node.type) return 1; // Default to 1

    const config = NODE_REGISTRY[node.type];
    if (!config) return 1;

    const inputDef = config.inputs.find(i => i.key === handleId);
    // If undefined, default to 1. If -1, treat as unlimited.
    const staticMaxConnections = inputDef?.maxConnections ?? 1;

    // Check if node has dynamic maxConnections in its data (e.g., for Gen Image node based on model)
    if (node.data?.maxConnections && typeof node.data.maxConnections === 'number') {
      return node.data.maxConnections;
    }

    return staticMaxConnections;
  }, [getNode]);

  // 1. Validator: Prevents dragging connections between incompatible handles
  const isValidConnection = useCallback((connection: Connection | Edge) => {
    // Prevent self-connections
    if (connection.source === connection.target) return false;

    const sourceNode = getNode(connection.source);
    const targetNode = getNode(connection.target);

    if (!sourceNode || !targetNode || !sourceNode.type || !targetNode.type) return false;

    const sourceConfig = NODE_REGISTRY[sourceNode.type];
    const targetConfig = NODE_REGISTRY[targetNode.type];

    if (!sourceConfig || !targetConfig) return false;

    const outputDef = sourceConfig.outputs.find(o => o.key === connection.sourceHandle);

    // For gen-video node, filter inputs based on current mode (same logic as BaseNode.tsx)
    let availableInputs = targetConfig.inputs;
    if (targetNode.type === 'gen-video') {
      const currentMode = targetNode.data.currentMode;

      if (currentMode) {
        const orderedInputs: string[] = ['in_text', 'in_text_negative'];

        // Add mode-specific inputs in fixed order
        // 🔴 更新：移除reference和extend模式，interpolate模式移除in_img_ref
        if (currentMode === 'image') {
          orderedInputs.push('in_img_ref');
        }
        if (currentMode === 'interpolate') {
          orderedInputs.push('in_img_first');
          orderedInputs.push('in_img_last');
        }

        // Filter inputs in the defined order
        availableInputs = orderedInputs
          .map(key => targetConfig.inputs.find(input => input.key === key))
          .filter((input): input is Exclude<typeof input, undefined> => input !== undefined);
      }
    }

    const inputDef = availableInputs.find(i => i.key === connection.targetHandle);

    if (!outputDef || !inputDef) return false;

    // Check if types match
    if (outputDef.type !== inputDef.type) return false;

    // For gen-video node, also check if the handle is visible in current mode
    if (targetNode.type === 'gen-video' && targetNode.data.currentMode) {
      const currentMode = targetNode.data.currentMode;
      const targetHandle = connection.targetHandle;

      // Determine if this handle should be visible
      const visibleInputs: string[] = ['in_text', 'in_text_negative'];
      if (currentMode === 'image') {
        visibleInputs.push('in_img_ref');
      }
      if (currentMode === 'interpolate') {
        visibleInputs.push('in_img_first');
        visibleInputs.push('in_img_last');
      }

      // Reject connection if handle is not visible
      if (!visibleInputs.includes(targetHandle)) {
        return false;
      }
    }

    return true;
  }, [getNode]);

  // 2. Connector: Adds the edge if valid AND handles Max Connection Enforcement (Auto-replace oldest)
  const onConnect = useCallback((params: Connection) => {
    const maxConnections = getHandleMaxConnections(params.target, params.targetHandle || '');

    setEdges((eds) => {
      let currentEdges = eds;

      // Logic for limited connections
      // If maxConnections is -1, it means infinite, so we don't delete anything.
      if (maxConnections !== -1) {
        const existingEdges = eds.filter(e =>
          e.target === params.target && e.targetHandle === params.targetHandle
        );

        if (existingEdges.length >= maxConnections) {
          // We need to add 1 new edge. So we must have at most (max - 1) edges before adding.
          // Calculate how many to remove: existing + 1 - max
          const countToRemove = existingEdges.length - maxConnections + 1;
          if (countToRemove > 0) {
            // Remove the oldest ones (first in array)
            const edgesToRemove = existingEdges.slice(0, countToRemove);
            const idsToRemove = new Set(edgesToRemove.map(e => e.id));
            currentEdges = eds.filter(e => !idsToRemove.has(e.id));
          }
        }
      }

      return addEdge({
        ...params,
        animated: true,
        style: { stroke: '#94a3b8', strokeWidth: 2 },
      }, currentEdges);
    });
  }, [setEdges, getHandleMaxConnections]);

  // 3. Edge Deletion Handlers
  const onEdgeDoubleClick = useCallback((evt: React.MouseEvent, edge: Edge) => {
    evt.stopPropagation();
    setEdges((eds) => eds.filter((e) => e.id !== edge.id));
  }, [setEdges]);

  const onEdgeContextMenu = useCallback((evt: React.MouseEvent, edge: Edge) => {
    evt.preventDefault(); // Prevent browser context menu
    evt.stopPropagation();
    setEdges((eds) => eds.filter((e) => e.id !== edge.id));
  }, [setEdges]);

  // Manual Add Node Helper
  const addNode = (type: string) => {
    const id = `${type}-${Date.now()}`;
    const offset = Math.random() * 100;
    const config = NODE_REGISTRY[type];

    const newNode: Node = {
      id,
      type,
      position: { x: 300 + offset, y: 300 + offset },
      data: { ...(config.defaultData || {}) },
      // Apply style if defined in registry
      style: (config.width || config.height) ? {
        width: config.width,
        height: config.height
      } : undefined,
    };
    setNodes((nds) => nds.concat(newNode));
    setIsAddMenuOpen(false);
  };

  // Auto Connect Logic via Menu
  const handleAutoConnect = (selectedNodeType: string) => {
    if (!menuData) return;

    const { nodeId, handleId, type: dataType, isInput } = menuData;
    const existingNode = nodes.find(n => n.id === nodeId);

    if (!existingNode) return;

    const newNodeId = `${selectedNodeType}-${Date.now()}`;
    let newPosition = { x: 0, y: 0 };
    let connectionParams: Connection;
    let shouldCheckConnectionLimit = false;
    const newNodeConfig = NODE_REGISTRY[selectedNodeType];

    const newNodeWidth = newNodeConfig.width || 300;
    const newNodeHeight = newNodeConfig.height || 200;

    if (isInput) {
      // CASE: Clicked INPUT handle on Existing Node.
      // We are creating a node that FEEDS INTO the existing node.
      // Existing Node is the TARGET. New Node is the SOURCE.
      shouldCheckConnectionLimit = true;

      // 1. Calculate Position (Place to the LEFT)
      const baseX = existingNode.position.x - 350;
      const baseY = existingNode.position.y;
      newPosition = findFreePosition(baseX, baseY, newNodeWidth, newNodeHeight);

      // 2. Find compatible output on the NEW Node
      const sourceOutput = newNodeConfig.outputs.find(o => o.type === dataType);

      if (!sourceOutput) {
        console.error("New node does not have a compatible output");
        return;
      }

      // 3. Define Edge: New Node -> Existing Node
      connectionParams = {
        source: newNodeId,
        sourceHandle: sourceOutput.key,
        target: nodeId,
        targetHandle: handleId,
      };

    } else {
      // CASE: Clicked OUTPUT handle on Existing Node.
      // Existing Node is the SOURCE. New Node is the TARGET.
      // 1. Calculate Position (Place to the RIGHT)
      const baseX = existingNode.position.x + (existingNode.measured?.width || 300) + 100;
      const baseY = existingNode.position.y;
      newPosition = findFreePosition(baseX, baseY, newNodeWidth, newNodeHeight);

      // 2. Find compatible input on the NEW Node
      const targetInput = newNodeConfig.inputs.find(i => i.type === dataType);

      if (!targetInput) {
        console.error("New node does not have a compatible input");
        return;
      }

      // 3. Define Edge: Existing Node -> New Node
      connectionParams = {
        source: nodeId,
        sourceHandle: handleId,
        target: newNodeId,
        targetHandle: targetInput.key,
      };
    }

    const newNode: Node = {
      id: newNodeId,
      type: selectedNodeType,
      position: newPosition,
      data: { ...(newNodeConfig.defaultData || {}) },
      // Apply style if defined in registry
      style: (newNodeConfig.width || newNodeConfig.height) ? {
        width: newNodeConfig.width,
        height: newNodeConfig.height
      } : undefined,
    };

    setNodes((nds) => nds.concat(newNode));

    setEdges((eds) => {
      let currentEdges = eds;

      // If we are connecting TO an input (isInput case), we must check the max connections on the existing node
      if (shouldCheckConnectionLimit) {
        const maxConnections = getHandleMaxConnections(nodeId, handleId);

        if (maxConnections !== -1) {
          const existingEdges = eds.filter(e => e.target === nodeId && e.targetHandle === handleId);

          if (existingEdges.length >= maxConnections) {
            // Remove oldest necessary to fit 1 new
            const countToRemove = existingEdges.length - maxConnections + 1;
            if (countToRemove > 0) {
              const edgesToRemove = existingEdges.slice(0, countToRemove);
              const idsToRemove = new Set(edgesToRemove.map(e => e.id));
              currentEdges = eds.filter(e => !idsToRemove.has(e.id));
            }
          }
        }
      }

      return addEdge({
        ...connectionParams,
        animated: true,
        style: { stroke: '#94a3b8', strokeWidth: 2 },
      }, currentEdges);
    });

    setMenuData(null);
  };

  // Context Provider Function
  const openMenu = useCallback((x: number, y: number, nodeId: string, handleId: string, type: DataType, isInput: boolean) => {
    setMenuData({ x, y, nodeId, handleId, type, isInput });
  }, []);

  // Copy, Paste, Delete handlers
  const handleCopy = useCallback(() => {
    const selectedNodes = nodes.filter(n => n.selected);
    const selectedNodeIds = new Set(selectedNodes.map(n => n.id));

    // If a group is selected, also include all its child nodes
    const childNodeIds = new Set<string>();
    selectedNodes.forEach(node => {
      if (node.type === 'group') {
        // Find all child nodes of this group
        nodes.filter(n => n.parentId === node.id).forEach(child => {
          childNodeIds.add(child.id);
        });
      }
    });

    // Combine selected nodes and child nodes
    const allNodeIds = new Set([...selectedNodeIds, ...childNodeIds]);
    const allNodes = nodes.filter(n => allNodeIds.has(n.id));

    // Only copy edges where both ends are in the copied nodes
    const selectedEdges = edges.filter(e =>
      allNodeIds.has(e.source) && allNodeIds.has(e.target)
    );

    setClipboard({
      nodes: allNodes,
      edges: selectedEdges,
    });
  }, [nodes, edges]);

  const handlePaste = useCallback(() => {
    if (!clipboard) return;

    const idMap = new Map<string, string>();
    const offsetX = 50;
    const offsetY = 50;

    // Separate groups and other nodes
    const groups = clipboard.nodes.filter(n => n.type === 'group');
    const otherNodes = clipboard.nodes.filter(n => n.type !== 'group');

    // Create new groups with offset positions
    const newGroups = groups.map(node => {
      const newId = `${node.type}-${Date.now()}-${Math.random()}`;
      idMap.set(node.id, newId);
      const { customLabel, ...dataWithoutLabel } = node.data;
      return {
        ...node,
        id: newId,
        position: {
          x: (node.position.x as number) + offsetX,
          y: (node.position.y as number) + offsetY,
        },
        selected: true,
        data: dataWithoutLabel,
      };
    });

    // Create new child nodes (keep relative positions, update parentId)
    const newChildNodes = otherNodes
      .filter(n => n.parentId)  // Only child nodes
      .map(node => {
        const newId = `${node.type}-${Date.now()}-${Math.random()}`;
        idMap.set(node.id, newId);
        const oldParentId = node.parentId!;
        const newParentId = idMap.get(oldParentId);
        const { customLabel, ...dataWithoutLabel } = node.data;
        return {
          ...node,
          id: newId,
          // If parent was copied, use new parent ID; otherwise keep old parent ID
          parentId: newParentId || oldParentId,
          position: {
            x: node.position.x,  // Keep relative position
            y: node.position.y,
          },
          selected: false,
          data: dataWithoutLabel,
        };
      });

    // Create new standalone nodes (offset positions)
    const newStandaloneNodes = otherNodes
      .filter(n => !n.parentId)  // Only standalone nodes
      .map(node => {
        const newId = `${node.type}-${Date.now()}-${Math.random()}`;
        idMap.set(node.id, newId);
        const { customLabel, ...dataWithoutLabel } = node.data;
        return {
          ...node,
          id: newId,
          position: {
            x: (node.position.x as number) + offsetX,
            y: (node.position.y as number) + offsetY,
          },
          selected: true,
          data: dataWithoutLabel,
        };
      });

    // Combine all new nodes
    const newNodes = [...newGroups, ...newChildNodes, ...newStandaloneNodes];

    // Create new edges with updated source/target IDs
    const newEdges = clipboard.edges.map(edge => ({
      ...edge,
      id: `${edge.source}-${edge.target}-${Date.now()}`,
      source: idMap.get(edge.source) || edge.source,
      target: idMap.get(edge.target) || edge.target,
      selected: false,
    }));

    // Deselect all existing nodes
    setNodes((nds) => nds.map(n => ({ ...n, selected: false })));

    // Add new nodes and edges
    setNodes((nds) => [...nds, ...newNodes]);
    setEdges((eds) => [...eds, ...newEdges]);
  }, [clipboard, setNodes, setEdges]);

  const handleDelete = useCallback(() => {
    const selectedNodes = nodes.filter(n => n.selected);
    const selectedNodeIds = new Set(selectedNodes.map(n => n.id));

    // If any group is selected, also select all its child nodes for deletion
    const childNodeIds = new Set<string>();
    selectedNodes.forEach(node => {
      if (node.type === 'group') {
        // Find all child nodes of this group
        nodes.filter(n => n.parentId === node.id).forEach(child => {
          childNodeIds.add(child.id);
        });
      }
    });

    // Combine selected node IDs and child node IDs
    const allNodeIdsToDelete = new Set([...selectedNodeIds, ...childNodeIds]);

    setNodes((nds) => nds.filter(n => !allNodeIdsToDelete.has(n.id)));
    setEdges((eds) => eds.filter(e =>
      !allNodeIdsToDelete.has(e.source) && !allNodeIdsToDelete.has(e.target)
    ));
  }, [nodes, setNodes, setEdges]);

  // Get ReactFlow instance for view manipulation
  const reactFlowInstance = useReactFlow();

  // Template import logic
  const handleImportTemplate = useCallback((template: WorkflowTemplate) => {
    const { config } = template;
    const { nodes: templateNodes, edges: templateEdges } = config;

    // 1. Generate ID mapping table
    const idMap: Record<string, string> = {};

    // 2. Calculate existing nodes' boundaries to determine import position
    const existingNodes = nodes;
    let maxX = 0;
    let maxY = 0;

    existingNodes.forEach(node => {
      const nodeX = (node.position.x as number) + (node.style?.width || node.measured?.width || 240);
      const nodeY = (node.position.y as number) + (node.style?.height || node.measured?.height || 200);
      maxX = Math.max(maxX, nodeX);
      maxY = Math.max(maxY, nodeY);
    });

    // 3. Calculate offset (add to the right of existing nodes with spacing)
    const offsetX = maxX + 100;
    const offsetY = 50;

    // 4. Process template nodes: generate new IDs + apply offset
    const newNodes = templateNodes.map(node => {
      const newId = `template-${template.id}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      idMap[node.id] = newId;

      // Calculate new position, keeping relative positions between nodes
      const newPosition = {
        x: (node.position.x as number) + offsetX,
        y: (node.position.y as number) + offsetY
      };

      // Remove potentially problematic fields
      const { selected, dragging, measured, ...nodeData } = node;

      return {
        ...nodeData,
        id: newId,
        position: newPosition,
        selected: true,
        dragging: false
      };
    });

    // 5. Process template edges: update source and target IDs
    const newEdges = templateEdges.map(edge => {
      const newId = `template-edge-${template.id}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

      return {
        ...edge,
        id: newId,
        source: idMap[edge.source],
        target: idMap[edge.target]
      };
    });

    // 6. Add to canvas: clear existing selections and add new nodes
    setNodes(nds => nds.map(node => ({ ...node, selected: false })).concat(newNodes));
    setEdges(eds => [...eds, ...newEdges]);

    // 7. Focus on newly imported nodes after they're added to the canvas
    // Use setTimeout to ensure nodes are rendered before focusing
    setTimeout(() => {
      if (reactFlowInstance && newNodes.length > 0) {
        // Calculate the bounding box of newly added nodes
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;

        // Find the bounding box of the new nodes
        newNodes.forEach(node => {
          // Only use style.width/height if available, otherwise use default values
          const nodeWidth = typeof node.style?.width === 'number' ? node.style.width : 240;
          const nodeHeight = typeof node.style?.height === 'number' ? node.style.height : 200;

          minX = Math.min(minX, node.position.x as number);
          minY = Math.min(minY, node.position.y as number);
          maxX = Math.max(maxX, (node.position.x as number) + nodeWidth);
          maxY = Math.max(maxY, (node.position.y as number) + nodeHeight);
        });

        // If we have valid coordinates, pan to the new nodes without zooming out too much
        if (minX < maxX && minY < maxY) {
          // Focus on the new nodes with minimal zoom adjustment
          reactFlowInstance.fitView({
            padding: 0.1, // Very small padding to avoid significant zoom out
            duration: 500, // Smooth animation
            // Convert node IDs to objects with id property for TypeScript compatibility
            nodes: newNodes.map(node => ({ id: node.id }))
          });
        }
      }
    }, 300); // Increased timeout to ensure nodes are fully rendered

    console.log('Template imported successfully:', {
      templateId: template.id,
      newNodes: newNodes.length,
      newEdges: newEdges.length
    });
  }, [nodes, setNodes, setEdges, reactFlowInstance]);

  const handleGroupNodes = useCallback(() => {
    const selectedNodes = nodes.filter(n => n.selected);

    if (selectedNodes.length < 2) return;

    const padding = 30; // Increased padding to separate border from child nodes
    const minX = Math.min(...selectedNodes.map(n => n.position.x));
    const minY = Math.min(...selectedNodes.map(n => n.position.y));
    const maxX = Math.max(...selectedNodes.map(n => n.position.x + (n.measured?.width || 200)));
    const maxY = Math.max(...selectedNodes.map(n => n.position.y + (n.measured?.height || 100)));

    const groupId = `group-${Date.now()}`;

    const groupNode: Node = {
      id: groupId,
      type: 'group',
      position: { x: minX - padding, y: minY - padding },
      data: { label: 'Group' },
      style: {
        width: maxX - minX + padding * 2,
        height: maxY - minY + padding * 2,
      },
      zIndex: -1,
    };

    const updatedNodes = nodes.map(n => {
      if (n.selected) {
        return {
          ...n,
          parentId: groupId,
          selected: false,
          position: {
            x: n.position.x - minX + padding,
            y: n.position.y - minY + padding,
          },
        };
      }
      return n;
    });

    setNodes([groupNode, ...updatedNodes]);
  }, [nodes, setNodes]);

  const handleUngroupNodes = useCallback(() => {
    const selectedGroups = nodes.filter(n => n.selected && n.type === 'group');

    if (selectedGroups.length === 0) return;

    const groupIds = new Set(selectedGroups.map(g => g.id));

    const updatedNodes = nodes.map(n => {
      if (n.parentId && groupIds.has(n.parentId)) {
        const parentNode = nodes.find(p => p.id === n.parentId);
        return {
          ...n,
          parentId: undefined,
          position: {
            x: n.position.x + (parentNode?.position.x || 0),
            y: n.position.y + (parentNode?.position.y || 0),
          },
        };
      }
      return n;
    });

    setNodes(updatedNodes.filter(n => !groupIds.has(n.id)));
  }, [nodes, setNodes]);

  const getSelectionBounds = useCallback(() => {
    const selectedNodes = nodes.filter(n => n.selected);

    if (selectedNodes.length === 0) return null;

    const minX = Math.min(...selectedNodes.map(n => n.position.x));
    const minY = Math.min(...selectedNodes.map(n => n.position.y));
    const maxX = Math.max(...selectedNodes.map(n => n.position.x + (n.measured?.width || 200)));
    const maxY = Math.max(...selectedNodes.map(n => n.position.y + (n.measured?.height || 100)));

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }, [nodes]);

  return (
    <CanvasContext.Provider value={{ openMenu }}>
      <FlowCanvasStyles />
      <div
        className="w-full h-full bg-gray-950 relative"
        onContextMenu={(e) => {
          e.preventDefault();
          setCanvasMenu({ x: e.clientX, y: e.clientY });
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          isValidConnection={isValidConnection}
          onEdgeDoubleClick={onEdgeDoubleClick}
          onEdgeContextMenu={onEdgeContextMenu}
          nodeTypes={nodeTypes}
          connectionMode={ConnectionMode.Loose}
          fitView
          fitViewOptions={{ padding: 0.9 }}
          zoomOnScroll={true}
          minZoom={0.1}
          maxZoom={3}
          onClick={() => {
            setMenuData(null);
            setCanvasMenu(null);
          }}
          onPaneClick={() => {
            setMenuData(null);
            setCanvasMenu(null);
          }}
          deleteKeyCode="Delete"
          panOnDrag={true}
          selectionKeyCode="Shift"
          multiSelectionKeyCode="Shift"
          className="react-flow-custom"
        >
          <Background color="#3d4a5c" gap={20} size={1} />

          {/* 统一的画布控制组件 */}
          <div
            className="absolute"
            style={{ bottom: '16px', left: '16px', zIndex: 5 }}
          >
            <CanvasControls onAutoLayout={handleAutoLayout} />
          </div>

          <MiniMap
            className="!bg-slate-900 border border-slate-700"
            nodeStrokeColor="#475569"
            nodeColor="#1e293b"
            maskColor="rgba(0,0,0, 0.4)"
          />
          <ControlPanel />

          {/* Context Menu Overlay */}
          {menuData && (
            <NodeContextMenu
              x={menuData.x}
              y={menuData.y}
              sourceType={menuData.type}
              isInput={menuData.isInput}
              onClose={() => setMenuData(null)}
              onSelect={handleAutoConnect}
            />
          )}

          {/* Canvas Context Menu */}
          {canvasMenu && (
            <CanvasContextMenu
              x={canvasMenu.x}
              y={canvasMenu.y}
              hasSelection={nodes.some(n => n.selected)}
              hasClipboard={clipboard !== null}
              hasGroupSelected={nodes.some(n => n.selected && n.type === 'group')}
              onClose={() => setCanvasMenu(null)}
              onCopy={handleCopy}
              onPaste={handlePaste}
              onDelete={handleDelete}
              onUngroup={handleUngroupNodes}
              onOpenWorkflows={() => setIsTemplatesDialogOpen(true)}
            />
          )}

          {/* Group Button - Show when 2+ nodes are selected */}
          {(() => {
            const selectedCount = nodes.filter(n => n.selected).length;
            if (selectedCount < 2) return null;

            const bounds = getSelectionBounds();
            if (!bounds) return null;

            const viewport = getViewport();
            const screenX = bounds.x * viewport.zoom + viewport.x;
            const screenY = bounds.y * viewport.zoom + viewport.y;
            const screenRight = screenX + bounds.width * viewport.zoom;
            const screenTop = screenY;

            return (
              <div
                className="absolute z-50"
                style={{
                  left: screenRight + 10,
                  top: screenTop - 10,
                  transform: 'translate(0, -100%)',
                }}
              >
                <button
                  onClick={handleGroupNodes}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg shadow-lg text-xs font-medium transition-colors border border-blue-500 whitespace-nowrap"
                >
                  Group ({selectedCount})
                </button>
              </div>
            );
          })()}

        </ReactFlow>

        {/* Bottom Action Bar */}
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-50 flex flex-col items-center gap-2">

          {/* Expanded Menu */}
          <div className={clsx(
            "flex items-center gap-2 bg-slate-800/90 backdrop-blur border border-slate-700 p-2 rounded-xl shadow-2xl transition-all duration-300 origin-bottom scale-90 opacity-0 pointer-events-none mb-2",
            isAddMenuOpen && "scale-100 opacity-100 pointer-events-auto"
          )}>
            <button onClick={() => { setIsTemplatesDialogOpen(true); setIsAddMenuOpen(false); }} className="flex flex-col items-center gap-1 p-2 hover:bg-slate-700 rounded text-slate-300 hover:text-white transition w-20">
              <BookOpen size={20} />
              <span className="text-[9px]">Workflows</span>
            </button>
            <div className="w-[1px] h-8 bg-slate-700"></div>
            <button onClick={() => addNode('text-node')} className="flex flex-col items-center gap-1 p-2 hover:bg-slate-700 rounded text-slate-300 hover:text-white transition w-20">
              <Type size={20} />
              <span className="text-[9px]">Text Input</span>
            </button>
            <button onClick={() => addNode('upload-image')} className="flex flex-col items-center gap-1 p-2 hover:bg-slate-700 rounded text-slate-300 hover:text-white transition w-20">
              <ImageIcon size={20} />
              <span className="text-[9px]">Upload Image</span>
            </button>
            <button onClick={() => addNode('text-label')} className="flex flex-col items-center gap-1 p-2 hover:bg-slate-700 rounded text-slate-300 hover:text-white transition w-20">
              <FileText size={20} />
              <span className="text-[9px]">Label</span>
            </button>
            <div className="w-[1px] h-8 bg-slate-700"></div>
            <button onClick={() => addNode('gen-image')} className="flex flex-col items-center gap-1 p-2 hover:bg-slate-700 rounded text-slate-300 hover:text-white transition w-20">
              <Wand2 size={20} />
              <span className="text-[9px]">Gen Image</span>
            </button>
            <button onClick={() => addNode('gen-video')} className="flex flex-col items-center gap-1 p-2 hover:bg-slate-700 rounded text-slate-300 hover:text-white transition w-20">
              <Video size={20} />
              <span className="text-[9px]">Gen Video</span>
            </button>
            <div className="w-[1px] h-8 bg-slate-700"></div>
            <button onClick={() => addNode('result-image')} className="flex flex-col items-center gap-1 p-2 hover:bg-slate-700 rounded text-slate-300 hover:text-white transition w-20">
              <ImageIcon size={20} className="text-blue-400" />
              <span className="text-[9px]">Image Result</span>
            </button>
            <button onClick={() => addNode('result-video')} className="flex flex-col items-center gap-1 p-2 hover:bg-slate-700 rounded text-slate-300 hover:text-white transition w-20">
              <Video size={20} className="text-purple-400" />
              <span className="text-[9px]">Video Result</span>
            </button>
          </div>

          {/* Toggle Button */}
          <button
            onClick={() => setIsAddMenuOpen(!isAddMenuOpen)}
            className={clsx(
              "w-12 h-12 flex items-center justify-center rounded-full shadow-lg shadow-blue-900/40 text-white transition-all duration-300",
              isAddMenuOpen ? "bg-red-500 rotate-45 hover:bg-red-600" : "bg-blue-600 hover:bg-blue-500 hover:scale-110"
            )}
          >
            <Plus size={24} strokeWidth={3} />
          </button>
        </div>

        {/* Workflow Templates Dialog */}
        <WorkflowTemplatesDialog
          isOpen={isTemplatesDialogOpen}
          onClose={() => setIsTemplatesDialogOpen(false)}
          onImportTemplate={handleImportTemplate}
        />

      </div>
    </CanvasContext.Provider>
  );
};

export default () => (
  <ReactFlowProvider>
    <Flow />
  </ReactFlowProvider>
);